import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getFunnelBySlug, trackFunnelConversion } from '../firebase/funnels';
import { getProduct } from '../firebase/products';
import { addSubscriber } from '../firebase/services';
import { Loader2, ArrowRight, CheckCircle, Gift, ShoppingCart, Mail, TrendingUp } from 'lucide-react';
import { FullPageLoader } from '../components/LoadingStates';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { validateForm, validators } from '../utils/validation';

export function FunnelPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [funnel, setFunnel] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [formErrors, setFormErrors] = useState({});
  const [orderBumpAccepted, setOrderBumpAccepted] = useState(false);
  const { handleError } = useErrorHandler();

  useEffect(() => {
    loadFunnel();
  }, [slug]);

  useEffect(() => {
    // Track page view
    if (funnel && currentStepIndex === 0) {
      trackFunnelConversion(funnel.id, funnel.steps[0]?.id, 'view');
    }
  }, [funnel, currentStepIndex]);

  const loadFunnel = async () => {
    setLoading(true);
    try {
      const funnelData = await getFunnelBySlug(slug);
      if (!funnelData) {
        navigate('/');
        return;
      }
      setFunnel(funnelData);
    } catch (error) {
      handleError(error, 'Loading funnel');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateStep = () => {
    const currentStep = funnel.steps[currentStepIndex];
    
    if (currentStep.type === 'optIn') {
      const validation = validateForm(formData, {
        name: [validators.required('Please enter your name')],
        email: [
          validators.required('Please enter your email'),
          validators.email('Please enter a valid email address')
        ]
      });
      
      if (!validation.isValid) {
        setFormErrors(validation.errors);
        return false;
      }
    }
    
    return true;
  };

  const handleNextStep = async () => {
    if (!validateStep()) return;
    
    setProcessing(true);
    const currentStep = funnel.steps[currentStepIndex];
    
    try {
      // Handle opt-in
      if (currentStep.type === 'optIn') {
        await addSubscriber(formData.name, formData.email);
        await trackFunnelConversion(funnel.id, currentStep.id, 'opt-in', {
          email: formData.email,
          name: formData.name
        });
      }
      
      // Move to next step
      if (currentStepIndex < funnel.steps.length - 1) {
        setCurrentStepIndex(currentStepIndex + 1);
        const nextStep = funnel.steps[currentStepIndex + 1];
        await trackFunnelConversion(funnel.id, nextStep.id, 'view');
      }
    } catch (error) {
      handleError(error, 'Processing step');
    } finally {
      setProcessing(false);
    }
  };

  const handlePurchase = async (productId, isUpsell = false) => {
    setProcessing(true);
    const currentStep = funnel.steps[currentStepIndex];
    
    try {
      const product = await getProduct(productId);
      if (!product) {
        throw new Error('Product not found');
      }

      // Store checkout items and funnel info in sessionStorage
      const checkoutItems = [{
        productId: product.id,
        title: product.title,
        description: product.description,
        price: parseFloat(product.price) || 0,
        quantity: 1,
        thumbnail: product.thumbnail
      }];

      sessionStorage.setItem('checkoutItems', JSON.stringify(checkoutItems));
      sessionStorage.setItem('checkoutCustomerEmail', formData.email || '');
      sessionStorage.setItem('checkoutCustomerName', formData.name || '');
      // Store which step index we're on so we can advance to next step
      sessionStorage.setItem('funnelReturnInfo', JSON.stringify({
        funnelId: funnel.id,
        stepId: currentStep.id,
        stepIndex: currentStepIndex,
        slug: slug,
        isUpsell: isUpsell,
        hasNextStep: currentStepIndex < funnel.steps.length - 1
      }));

      // Track conversion attempt
      await trackFunnelConversion(
        funnel.id,
        currentStep.id,
        isUpsell ? 'upsell-accept' : 'purchase-attempt',
        {
          productId: product.id,
          total: parseFloat(product.price) || 0
        }
      );

      // Redirect to checkout page
      navigate('/checkout');
    } catch (error) {
      handleError(error, 'Processing purchase');
      setProcessing(false);
    }
  };

  const handleUpsellDecline = async () => {
    const currentStep = funnel.steps[currentStepIndex];
    await trackFunnelConversion(funnel.id, currentStep.id, 'upsell-decline');
    
    // Move to next step or complete
    if (currentStepIndex < funnel.steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      // Funnel complete
      navigate('/');
    }
  };

  if (loading) {
    return <FullPageLoader message="Loading funnel..." />;
  }

  if (!funnel || !funnel.steps || funnel.steps.length === 0) {
    return (
      <div className="min-h-screen bg-[#fdfbf7] flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-gray-600">Funnel not found or has no steps.</p>
        </div>
      </div>
    );
  }

  const currentStep = funnel.steps[currentStepIndex];
  const isLastStep = currentStepIndex === funnel.steps.length - 1;

  return (
    <div className="min-h-screen bg-[#fdfbf7]">
      {/* Progress Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">
              Step {currentStepIndex + 1} of {funnel.steps.length}
            </span>
            <span className="text-sm text-gray-600">
              {Math.round(((currentStepIndex + 1) / funnel.steps.length) * 100)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-[#c5a059] h-2 rounded-full transition-all duration-500"
              style={{ width: `${((currentStepIndex + 1) / funnel.steps.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Opt-In Step */}
        {currentStep.type === 'optIn' && (
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 md:p-12 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="text-blue-600" size={32} />
            </div>
            
            <h1 className="text-4xl md:text-5xl font-serif text-gray-800 mb-4">
              {currentStep.headline || 'Get Your Free Guide'}
            </h1>
            
            {currentStep.description && (
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                {currentStep.description}
              </p>
            )}

            <div className="max-w-md mx-auto space-y-4">
              <div>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Your Name"
                  className={`w-full px-6 py-4 rounded-lg border-2 text-lg focus:outline-none focus:ring-2 focus:ring-[#c5a059] transition ${
                    formErrors.name ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {formErrors.name && (
                  <p className="text-red-600 text-sm mt-1 text-left">{formErrors.name}</p>
                )}
              </div>
              
              <div>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="Your Email"
                  className={`w-full px-6 py-4 rounded-lg border-2 text-lg focus:outline-none focus:ring-2 focus:ring-[#c5a059] transition ${
                    formErrors.email ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {formErrors.email && (
                  <p className="text-red-600 text-sm mt-1 text-left">{formErrors.email}</p>
                )}
              </div>

              <button
                onClick={handleNextStep}
                disabled={processing}
                className="w-full px-8 py-4 bg-[#c5a059] hover:bg-[#b08e4d] text-white text-lg font-semibold rounded-lg transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {processing ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    Get Access Now
                    <ArrowRight size={20} />
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Tripwire/Main Offer/Upsell Step */}
        {(currentStep.type === 'tripwire' || currentStep.type === 'mainOffer' || currentStep.type === 'upsell') && (
          <OfferStep
            step={currentStep}
            onPurchase={() => handlePurchase(currentStep.productId, currentStep.type === 'upsell')}
            onDecline={currentStep.type === 'upsell' ? handleUpsellDecline : null}
            processing={processing}
            orderBumpAccepted={orderBumpAccepted}
            onOrderBumpChange={setOrderBumpAccepted}
          />
        )}
      </div>
    </div>
  );
}

// Offer Step Component
function OfferStep({ step, onPurchase, onDecline, processing, orderBumpAccepted, onOrderBumpChange }) {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProduct();
  }, [step.productId]);

  const loadProduct = async () => {
    if (!step.productId) {
      setLoading(false);
      return;
    }
    try {
      const productData = await getProduct(step.productId);
      setProduct(productData);
    } catch (error) {
      console.error('Error loading product:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <FullPageLoader message="Loading offer..." />;
  }

  if (!product) {
    return (
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 text-center">
        <p className="text-gray-600">Product not found.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
      {product.thumbnail && (
        <img
          src={product.thumbnail}
          alt={product.title}
          className="w-full h-64 md:h-96 object-cover"
        />
      )}
      
      <div className="p-8 md:p-12">
        <h1 className="text-4xl md:text-5xl font-serif text-gray-800 mb-4 text-center">
          {step.headline || product.title}
        </h1>
        
        {step.description && (
          <p className="text-xl text-gray-600 mb-8 text-center max-w-2xl mx-auto">
            {step.description}
          </p>
        )}

        {product.description && (
          <div className="prose prose-lg max-w-none mb-8" dangerouslySetInnerHTML={{ __html: product.description }} />
        )}

        {/* Order Bump */}
        {step.orderBump && step.orderBump.productId && (
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6 mb-8">
            <label className="flex items-start gap-4 cursor-pointer">
              <input
                type="checkbox"
                checked={orderBumpAccepted}
                onChange={(e) => onOrderBumpChange(e.target.checked)}
                className="mt-1 w-5 h-5 text-[#c5a059] rounded focus:ring-[#c5a059]"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Gift className="text-yellow-600" size={20} />
                  <span className="font-semibold text-gray-800">Order Bump</span>
                </div>
                <p className="text-gray-700">{step.orderBump.title}</p>
              </div>
            </label>
          </div>
        )}

        {/* Pricing */}
        <div className="text-center mb-8">
          <div className="inline-block">
            {(() => {
              const price = parseFloat(product.price) || 0;
              if (price === 0) {
                return <p className="text-2xl font-semibold text-green-600">FREE</p>;
              }
              return <p className="text-5xl font-bold text-[#c5a059] mb-2">£{price.toFixed(2)}</p>;
            })()}
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
          <button
            onClick={onPurchase}
            disabled={processing}
            className="flex-1 px-8 py-4 bg-[#c5a059] hover:bg-[#b08e4d] text-white text-lg font-semibold rounded-lg transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {processing ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                Processing...
              </>
            ) : (
              <>
                {step.type === 'upsell' ? 'Yes, Add This!' : 'Get It Now'}
                <ShoppingCart size={20} />
              </>
            )}
          </button>
          
          {onDecline && (
            <button
              onClick={onDecline}
              disabled={processing}
              className="flex-1 px-8 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 text-lg font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              No Thanks
            </button>
          )}
        </div>

        {/* Trust Elements */}
        <div className="mt-8 text-center space-y-2">
          <div className="flex items-center justify-center gap-2 text-gray-600">
            <CheckCircle className="text-green-600" size={20} />
            <span>Instant Access</span>
          </div>
          <div className="flex items-center justify-center gap-2 text-gray-600">
            <CheckCircle className="text-green-600" size={20} />
            <span>Secure Payment</span>
          </div>
        </div>
      </div>
    </div>
  );
}



